import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Plus, Search, ArrowLeft, Save, User, BookMarked, Mail, Shield, Trash2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { successToast } from '../../lib/successToast';
import { HomeUniLoader } from '../../components/HomeUniLoader';
import { AvatarUpload } from '../../components/ui/AvatarUpload';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

const ACADEMIC_YEARS = ['2025-2026', '2026-2027', '2027-2028', '2028-2029', '2029-2030', '2030-2031', '2031-2032'];
const ADMISSION_WINDOWS = ['JAN', 'APR', 'JUL', 'OCT'];


const COHORT_COLORS = { JAN: 'bg-blue-100 text-blue-700', APR: 'bg-[#1976D2]/10 text-[#1976D2]', JUL: 'bg-indigo-100 text-indigo-700', OCT: 'bg-purple-100 text-purple-700' };

export default function StudentRegistry() {
  const [programmeCounts, setProgrammeCounts] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState('__all__');
  const [programmeList, setProgrammeList] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [profileTab, setProfileTab] = useState('details');
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, title: '', description: '', onConfirm: null, variant: 'danger' });
  const [addForm, setAddForm] = useState({
    name: '', email: '', academic_year: '2025-2026',
    programme: '', programme_id: '', admission_window: 'JAN', phone: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const [pcRes, progRes] = await Promise.all([
        api.get('/admin/students-by-programme'),
        api.get('/academic/programmes'),
      ]);
      setProgrammeCounts(pcRes.data);
      setProgrammeList(progRes.data);
    } catch (err) { console.error("Error:", err); }
    setLoading(false);
  }, []);

  const fetchStudents = useCallback(async (progId) => {
    try {
      const params = {};
      if (progId && progId !== '__all__') params.programme = progId;
      const res = await api.get('/admin/students', { params });
      setStudents(res.data);
    } catch (err) { console.error("Error:", err); }
  }, []);

  const fetchCoursesForProg = useCallback(async (progId) => {
    if (!progId) { setCourses([]); return; }
    try {
      const res = await api.get(`/academic/programmes/${progId}/courses`);
      setCourses(res.data);
    } catch { setCourses([]); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchStudents(selectedProgramme); }, [selectedProgramme, fetchStudents]);

  const openProfile = async (student) => {
    try {
      const res = await api.get(`/admin/students/${student.user_id}`);
      const s = res.data;
      const enrolled = s.enrolled_programmes || [];
      const existingIds = enrolled.map(ep => ep.programme_id).filter(Boolean);
      setSelectedStudent(s);
      setEditForm({
        name: s.name || '', phone: s.phone || '',
        academic_year: s.academic_year || '',
        programme: s.programme || '',
        admission_window: s.admission_window || 'JAN',
        date_of_birth: s.date_of_birth || '',
        admission_number: s.admission_number || '',
        is_active: s.is_active !== false,
        subject_ids: s.subject_ids || [],
        enrolled_programmes: enrolled,
        programme_ids: existingIds,
        email: s.email || '',
      });
      if (enrolled.length > 0) fetchCoursesForProg(enrolled[0].programme_id);
      setProfileTab('details');
    } catch { toast.error('Failed to load student profile'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const selectedIds = editForm.programme_ids || [];
      const updatedEnrolled = selectedIds.map(pid => {
        const existing = (editForm.enrolled_programmes || []).find(ep => ep.programme_id === pid);
        if (existing) return existing;
        const prog = programmeList.find(p => p.programme_id === pid);
        return {
          programme_id: pid,
          programme_name: prog?.name || '',
          admission_window: editForm.admission_window || 'JAN',
          cohort: `${editForm.admission_window || 'JAN'} 2026`,
          enrolled_at: new Date().toISOString(),
          payment_ref: 'admin-assigned',
          status: 'active',
        };
      });
      const firstProg = programmeList.find(p => p.programme_id === selectedIds[0]);
      const payload = {
        ...editForm,
        enrolled_programmes: updatedEnrolled,
        programme: firstProg?.name || editForm.programme,
      };
      await api.put(`/admin/students/${selectedStudent.user_id}`, payload);
      successToast('Student profile updated');
      setEditForm(f => ({ ...f, enrolled_programmes: updatedEnrolled }));
      fetchStudents(selectedProgramme);
      fetchData();
      setSelectedStudent(s => ({ ...s, ...payload }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    setSaving(false);
  };

  const handleDeleteStudent = () => {
    setConfirm({
      open: true,
      title: `Delete "${selectedStudent.name}"?`,
      description: 'This will permanently remove all their invoices, payments, grades, and submissions. This cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const res = await api.delete(`/admin/students/${selectedStudent.user_id}`);
          successToast(res.data.message);
          setSelectedStudent(null);
          fetchStudents(selectedProgramme);
          fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
      },
    });
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/students', addForm);
      successToast('Student added');
      setDialogOpen(false);
      setAddForm({ name: '', email: '', academic_year: '2025-2026', programme: '', programme_id: '', admission_window: 'JAN', phone: '' });
      fetchStudents(selectedProgramme);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const enrolCount = (editForm.enrolled_programmes || []).length;

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.admission_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.programme?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <HomeUniLoader />;

  // ─── Student Profile View ───
  if (selectedStudent) {
    const window = selectedStudent.admission_window || 'JAN';
    return (
      <>
      <div className="space-y-6" data-testid="student-profile">
        <Button variant="ghost" size="sm" onClick={() => setSelectedStudent(null)} data-testid="back-to-students">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Students
        </Button>

        <div className="bg-white rounded-xl border border-[#BBDEFB] p-6">
          <div className="flex items-center gap-5">
            <AvatarUpload
              userId={selectedStudent.user_id}
              name={selectedStudent.name}
              pictureUrl={selectedStudent.picture}
              size="w-16 h-16"
              onUploaded={(url) => setSelectedStudent(s => ({ ...s, picture: url }))}
            />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-[#1B2A5E]" data-testid="student-profile-name">{selectedStudent.name}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <Badge className="bg-blue-100 text-blue-700">{selectedStudent.academic_year}</Badge>
                {selectedStudent.programme && <Badge className="bg-[#EFF6FF] text-[#1565C0] border border-[#BBDEFB]">{selectedStudent.programme}</Badge>}
                <Badge className={COHORT_COLORS[window] || 'bg-slate-100 text-slate-600'}>{window} Cohort</Badge>
                <Badge variant="secondary">{selectedStudent.admission_number}</Badge>
                <Badge className={selectedStudent.is_active !== false ? 'bg-[#1976D2]/15 text-[#1976D2]' : 'bg-red-100 text-red-700'}>
                  {selectedStudent.is_active !== false ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-xs text-slate-400 mt-1">{selectedStudent.email} · {selectedStudent.subjects?.length || 0} courses enrolled</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="rounded-xl btn-navy" data-testid="save-student-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <Tabs value={profileTab} onValueChange={setProfileTab}>
          <TabsList className="bg-white border border-[#BBDEFB]">
            <TabsTrigger value="details" className="text-xs">Personal Details</TabsTrigger>
            <TabsTrigger value="courses" className="text-xs">Programmes ({enrolCount})</TabsTrigger>
            <TabsTrigger value="account" className="text-xs">Account</TabsTrigger>
          </TabsList>

          {/* Details */}
          <TabsContent value="details" className="mt-4">
            <Card className="rounded-xl border-[#BBDEFB]">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="w-5 h-5 text-[#1565C0]" /> Student Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Full Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="rounded-lg" data-testid="edit-student-name" /></div>
                  <div className="space-y-2"><Label>Admission Number</Label><Input value={editForm.admission_number} onChange={e => setEditForm({...editForm, admission_number: e.target.value})} className="rounded-lg" data-testid="edit-admission" /></div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Programmes Enrolled</Label>
                    <span className="text-[10px] text-slate-400">Double-click to enrol / remove</span>
                  </div>
                  <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-[#BBDEFB] bg-[#EFF6FF]/40 min-h-[52px]">
                    {(programmeList.length === 0) && (
                      <p className="text-xs text-slate-400 self-center">Loading programmes…</p>
                    )}
                    {programmeList.map(prog => {
                      const selected = (editForm.programme_ids || []).includes(prog.programme_id);
                      return (
                        <button
                          key={prog.programme_id}
                          type="button"
                          onDoubleClick={() => {
                            const ids = editForm.programme_ids || [];
                            setEditForm({
                              ...editForm,
                              programme_ids: selected
                                ? ids.filter(id => id !== prog.programme_id)
                                : [...ids, prog.programme_id],
                            });
                          }}
                          title={`${prog.name} — double-click to ${selected ? 'remove' : 'enrol'}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all select-none cursor-pointer
                            ${selected
                              ? 'bg-[#1565C0] text-white border-[#1565C0] shadow-sm'
                              : 'bg-white text-slate-600 border-[#BBDEFB] hover:border-[#1565C0]/50 hover:text-[#1565C0]'
                            }`}
                        >
                          {prog.code ? `${prog.code} · ` : ''}{prog.name}
                        </button>
                      );
                    })}
                  </div>
                  {(editForm.programme_ids || []).length > 0 && (
                    <p className="text-[11px] text-[#1565C0]">
                      {(editForm.programme_ids || []).length} programme{(editForm.programme_ids || []).length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Academic Year</Label>
                    <Select value={editForm.academic_year} onValueChange={v => setEditForm({...editForm, academic_year: v})}>
                      <SelectTrigger className="rounded-lg" data-testid="edit-year-group"><SelectValue /></SelectTrigger>
                      <SelectContent>{ACADEMIC_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Admission Cohort</Label>
                    <Select value={editForm.admission_window} onValueChange={v => setEditForm({...editForm, admission_window: v})}>
                      <SelectTrigger className="rounded-lg" data-testid="edit-cohort"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ADMISSION_WINDOWS.map(w => <SelectItem key={w} value={w}>{w} Intake</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={editForm.date_of_birth} onChange={e => setEditForm({...editForm, date_of_birth: e.target.value})} className="rounded-lg" data-testid="edit-dob" /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="rounded-lg" data-testid="edit-student-phone" /></div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Courses / Programmes */}
          <TabsContent value="courses" className="mt-4">
            <Card className="rounded-xl border-[#BBDEFB]">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#1565C0]" /> Enrolled Programmes & Courses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(editForm.enrolled_programmes || []).length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Not enrolled in any programme yet</p>
                    <p className="text-xs mt-1">Student must enrol via the student portal using Paystack</p>
                  </div>
                ) : (editForm.enrolled_programmes || []).map((ep, i) => (
                  <div key={i} className="rounded-xl border border-[#BBDEFB] p-4 bg-[#EFF6FF]/30">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-[#1B2A5E]">{ep.programme_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={COHORT_COLORS[ep.admission_window] || 'bg-slate-100 text-slate-600'}>{ep.admission_window} Cohort</Badge>
                          <Badge className={ep.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>{ep.status || 'active'}</Badge>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-400">{ep.enrolled_at ? new Date(ep.enrolled_at).toLocaleDateString('en-GB') : ''}</p>
                    </div>
                    {courses.length > 0 && (
                      <div className="mt-2 grid grid-cols-2 gap-1.5">
                        {courses.map(c => (
                          <div key={c.course_id} className="flex items-center gap-2 text-xs text-slate-600 bg-white rounded-lg px-2 py-1 border border-[#BBDEFB]">
                            <BookMarked className="w-3 h-3 text-[#1565C0] flex-shrink-0" /> <span className="truncate">{c.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Account */}
          <TabsContent value="account" className="mt-4">
            <Card className="rounded-xl border-[#BBDEFB]">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Shield className="w-5 h-5 text-[#1565C0]" /> Account Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#EFF6FF] rounded-lg">
                  <div><p className="text-sm font-medium">Account Active</p><p className="text-xs text-slate-500">Deactivating prevents login</p></div>
                  <Switch checked={editForm.is_active} onCheckedChange={v => setEditForm({...editForm, is_active: v})} data-testid="toggle-student-active" />
                </div>
                <div className="p-4 bg-slate-50 rounded-lg"><p className="text-sm font-medium">User ID</p><p className="text-xs text-slate-400 font-mono">{selectedStudent.user_id}</p></div>
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-[#1565C0]" /> Login Email</Label>
                  <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="rounded-lg" placeholder="student@homeuni.edu.gh" data-testid="edit-student-email" />
                  <p className="text-[10px] text-slate-400">Changing this will update the student's login email</p>
                </div>
                <div className="border-t border-red-200 pt-4">
                  <p className="text-sm font-medium text-red-600 mb-2">Danger Zone</p>
                  <Button variant="outline" className="rounded-lg border-red-300 text-red-600 hover:bg-red-50 text-xs" onClick={handleDeleteStudent} data-testid="delete-student-btn">
                    <Trash2 className="w-3 h-3 mr-2" /> Delete Student Permanently
                  </Button>
                  <p className="text-[10px] text-slate-400 mt-1">Removes student and all related records. This cannot be undone.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <ConfirmDialog
        open={confirm.open}
        onOpenChange={o => setConfirm(c => ({ ...c, open: o }))}
        title={confirm.title}
        description={confirm.description}
        variant={confirm.variant}
        onConfirm={confirm.onConfirm}
      />
      </>
    );
  }

  // ─── Main List View ───
  return (
    <div className="space-y-6" data-testid="student-registry">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1B2A5E]">Student Registry</h1>
          <p className="text-slate-500 mt-1">{programmeCounts.reduce((s, p) => s + p.count, 0)} students enrolled across {programmeCounts.filter(p => p.programme_id).length} programmes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl btn-navy" data-testid="add-student-btn"><Plus className="w-4 h-4 mr-2" /> Add Student</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Student</DialogTitle></DialogHeader>
            <form onSubmit={handleAddStudent} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Full Name</Label><Input value={addForm.name} onChange={e => setAddForm({...addForm, name: e.target.value})} required data-testid="new-student-name" /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} required data-testid="new-student-email" /></div>
              </div>
              <div className="space-y-2"><Label>Programme</Label>
                <Select value={addForm.programme} onValueChange={v => {
                  const prog = programmeList.find(p => p.name === v);
                  setAddForm({...addForm, programme: v, programme_id: prog?.programme_id || ''});
                }}>
                  <SelectTrigger><SelectValue placeholder="Select programme" /></SelectTrigger>
                  <SelectContent>
                    {programmeList.map(p => <SelectItem key={p.programme_id} value={p.name}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Academic Year</Label>
                  <Select value={addForm.academic_year} onValueChange={v => setAddForm({...addForm, academic_year: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ACADEMIC_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Admission Cohort</Label>
                  <Select value={addForm.admission_window} onValueChange={v => setAddForm({...addForm, admission_window: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ADMISSION_WINDOWS.map(w => <SelectItem key={w} value={w}>{w} Intake</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Phone (optional)</Label><Input value={addForm.phone} onChange={e => setAddForm({...addForm, phone: e.target.value})} data-testid="new-student-phone" /></div>
              <Button type="submit" className="w-full rounded-xl btn-navy" data-testid="submit-new-student">Add Student</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Programme filter cards */}
      <div className="flex gap-3 overflow-x-auto pb-1">
        <button
          onClick={() => { setSelectedProgramme('__all__'); setSearchTerm(''); }}
          className={`flex-shrink-0 px-4 py-3 rounded-xl border-2 text-center transition-all ${selectedProgramme === '__all__' ? 'border-[#1565C0] bg-[#EFF6FF]' : 'border-[#BBDEFB] bg-white hover:border-[#1565C0]/40'}`}
        >
          <p className={`text-lg font-bold ${selectedProgramme === '__all__' ? 'text-[#1565C0]' : 'text-[#1B2A5E]'}`}>{programmeCounts.reduce((s, p) => s + p.count, 0)}</p>
          <p className="text-xs text-slate-500">All Students</p>
        </button>
        {programmeCounts.map(pc => (
          <button
            key={pc.programme_id || pc.programme_name}
            onClick={() => { setSelectedProgramme(pc.programme_id || '__all__'); setSearchTerm(''); }}
            className={`flex-shrink-0 px-4 py-3 rounded-xl border-2 text-center transition-all min-w-[120px] ${selectedProgramme === pc.programme_id ? 'border-[#1565C0] bg-[#EFF6FF]' : 'border-[#BBDEFB] bg-white hover:border-[#1565C0]/40'}`}
          >
            <p className={`text-lg font-bold ${selectedProgramme === pc.programme_id ? 'text-[#1565C0]' : 'text-[#1B2A5E]'}`}>{pc.count}</p>
            <p className="text-xs text-slate-500 truncate max-w-[100px] mx-auto" title={pc.programme_name}>{pc.programme_name?.split(' ').slice(0, 2).join(' ')}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white rounded-xl border border-[#BBDEFB] px-3 py-2 max-w-md">
        <Search className="w-4 h-4 text-slate-400" />
        <input type="text" placeholder="Search students by name, ADM no., or programme..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-transparent border-none outline-none text-sm w-full" data-testid="student-search" />
      </div>

      {/* Student table */}
      <div className="bg-white rounded-xl border border-[#BBDEFB] overflow-hidden">
        <div className="px-4 py-3 bg-[#EFF6FF] border-b border-[#BBDEFB] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#1B2A5E]">{selectedProgramme === '__all__' ? 'All Students' : (programmeCounts.find(p => p.programme_id === selectedProgramme)?.programme_name || 'Students')} — {filtered.length} students</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>ADM No.</TableHead>
              <TableHead>Programme</TableHead>
              <TableHead>Cohort</TableHead>
              <TableHead>Courses</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((s, idx) => (
              <TableRow
                key={s.user_id}
                className="hover:bg-[#EFF6FF]/50 cursor-pointer transition-colors"
                onClick={() => openProfile(s)}
                data-testid={`student-row-${s.user_id}`}
              >
                <TableCell className="text-xs text-slate-400 w-10">{idx + 1}</TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#EFF6FF] border border-[#BBDEFB] flex items-center justify-center text-[#1565C0] text-sm font-semibold">{s.name?.charAt(0)}</div>
                    <span className="hover:text-[#1565C0] transition-colors">{s.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-slate-500 text-sm">{s.admission_number || '-'}</TableCell>
                <TableCell className="text-xs text-slate-600 max-w-[200px] truncate">{s.programme || <span className="text-slate-300">—</span>}</TableCell>
                <TableCell>
                  {s.admission_window
                    ? <Badge className={COHORT_COLORS[s.admission_window] || 'bg-slate-100 text-slate-600'}>{s.admission_window}</Badge>
                    : <span className="text-slate-300 text-xs">—</span>}
                </TableCell>
                <TableCell className="text-xs text-slate-400">{s.subject_ids?.length || 0} courses</TableCell>
                <TableCell className="w-10" onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-300 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-student-list-${s.user_id}`}
                    onClick={() => setConfirm({
                      open: true,
                      title: `Delete "${s.name}"?`,
                      description: 'All invoices, payments, and grades will be permanently removed. This cannot be undone.',
                      variant: 'danger',
                      onConfirm: async () => {
                        try {
                          await api.delete(`/admin/students/${s.user_id}`);
                          successToast(`${s.name} deleted`);
                          fetchStudents(selectedProgramme);
                          fetchData();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      },
                    })}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No students found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <ConfirmDialog
        open={confirm.open}
        onOpenChange={o => setConfirm(c => ({ ...c, open: o }))}
        title={confirm.title}
        description={confirm.description}
        variant={confirm.variant}
        onConfirm={confirm.onConfirm}
      />
    </div>
  );
}
